import type { FlowDefinition, FlowBlock } from '../../types/flow';
import { getAllMethods } from './toolRegistryData';

const VALID_METHODS = new Set(getAllMethods());

export interface ValidationResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
}

export function validateFlow(flow: FlowDefinition): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (!flow.id || flow.id.trim() === '') {
    errors.push('Flow ID is required');
  }
  if (!flow.name || flow.name.trim() === '') {
    errors.push('Flow name is required');
  }
  if (flow.nodes.length === 0) {
    errors.push('Flow must have at least one node');
  }

  if (flow.entryNode && !flow.nodes.some((n) => n.id === flow.entryNode)) {
    errors.push(`Entry node "${flow.entryNode}" does not exist in nodes`);
  }
  if (!flow.entryNode && flow.nodes.length > 0) {
    warnings.push('No entry node set — first node will be used as entry');
  }

  const nodeIds = new Set(flow.nodes.map((n) => n.id));
  const duplicateIds = flow.nodes
    .map((n) => n.id)
    .filter((id, i, arr) => arr.indexOf(id) !== i);
  if (duplicateIds.length > 0) {
    errors.push(`Duplicate node IDs: ${duplicateIds.join(', ')}`);
  }

  for (const edge of flow.edges) {
    if (!nodeIds.has(edge.from)) {
      errors.push(`Edge from unknown node: "${edge.from}"`);
    }
    if (!nodeIds.has(edge.to)) {
      errors.push(`Edge to unknown node: "${edge.to}"`);
    }
  }

  const nodesWithIncoming = new Set(flow.edges.map((e) => e.to));
  const disconnected = flow.nodes.filter((n) => {
    if (n.id === flow.entryNode) return false;
    if (flow.nodes.length === 1) return false;
    return !nodesWithIncoming.has(n.id);
  });
  if (disconnected.length > 0) {
    warnings.push(`Nodes with no incoming edges: ${disconnected.map((n) => n.id).join(', ')}`);
  }

  const entryReachable = new Set<string>();
  if (flow.entryNode) {
    const queue = [flow.entryNode];
    while (queue.length > 0) {
      const current = queue.shift()!;
      if (entryReachable.has(current)) continue;
      entryReachable.add(current);
      for (const edge of flow.edges) {
        if (edge.from === current && !entryReachable.has(edge.to)) {
          queue.push(edge.to);
        }
      }
    }
  }
  const unreachable = flow.nodes.filter((n) => !entryReachable.has(n.id));
  if (unreachable.length > 0) {
    warnings.push(`Nodes not reachable from entry: ${unreachable.map((n) => n.id).join(', ')}`);
  }

  const hasOutput = flow.nodes.some((n) => n.type === 'output');
  if (!hasOutput) {
    warnings.push('Flow has no output block — the final agent response will be used');
  }

  for (const node of flow.nodes) {
    if (node.type === 'agent') {
      const agent = node as Extract<FlowBlock, { type: 'agent' }>;
      if (!agent.role) {
        warnings.push(`Agent block "${node.id}" has no role assigned`);
      }
      if (!agent.allowedMethods || agent.allowedMethods.length === 0) {
        warnings.push(`Agent block "${node.id}" has no allowedMethods — will use role defaults`);
      }
    }
    if (node.type === 'function') {
      const fn = node as Extract<FlowBlock, { type: 'function' }>;
      if (!fn.method) {
        errors.push(`Function block "${node.id}" has no method`);
      } else if (!VALID_METHODS.has(fn.method)) {
        warnings.push(`Function block "${node.id}" method "${fn.method}" is not a recognized handler`);
      }
    }
    if (node.type === 'router') {
      const router = node as Extract<FlowBlock, { type: 'router' }>;
      if (!router.branches || Object.keys(router.branches).length === 0) {
        errors.push(`Router block "${node.id}" has no branches`);
      }
      for (const [branch, target] of Object.entries(router.branches)) {
        if (!nodeIds.has(target)) {
          errors.push(`Router "${node.id}" branch "${branch}" points to unknown node: "${target}"`);
        }
      }
    }
    if (node.type === 'memory') {
      const mem = node as Extract<FlowBlock, { type: 'memory' }>;
      if (!mem.action) {
        errors.push(`Memory block "${node.id}" has no action (extract or retrieve)`);
      }
    }
    if (node.type === 'set') {
      const set = node as Extract<FlowBlock, { type: 'set' }>;
      if (!set.values || Object.keys(set.values).length === 0) {
        warnings.push(`Set block "${node.id}" has no values — it will produce empty output`);
      }
    }
    if (node.type === 'code') {
      const code = node as Extract<FlowBlock, { type: 'code' }>;
      if (!code.code || code.code.trim() === '') {
        errors.push(`Code block "${node.id}" has no code`);
      }
    }
    if (node.type === 'compaction') {
      // Compaction blocks are always valid — threshold/keepRecent are optional
    }
    if (node.type === 'transform') {
      const transform = node as Extract<FlowBlock, { type: 'transform' }>;
      if (!transform.mapping || Object.keys(transform.mapping).length === 0) {
        warnings.push(`Transform block "${node.id}" has no mappings — it will produce empty output`);
      }
    }
  }

  const routerEdges = flow.edges.filter((e) => e.condition);
  const routerNodes = flow.nodes.filter((n) => n.type === 'router');
  for (const router of routerNodes) {
    const r = router as Extract<FlowBlock, { type: 'router' }>;
    const branchValues = new Set(Object.values(r.branches));
    const conditionsWithMatchingEdge = routerEdges.filter(
      (e) => e.from === router.id && branchValues.has(e.to),
    );
    if (conditionsWithMatchingEdge.length < branchValues.size) {
      warnings.push(`Router "${router.id}" has branches without matching conditional edges`);
    }
  }

  return { valid: errors.length === 0, errors, warnings };
}